import React, {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import './Mailchimp.less';
import { CopyOutlined, QuestionOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
} from 'antd';
import 'highlight.js/styles/github.css';
import { CustomModal } from 'components';
import { copyElementToClipboard } from 'util/copy-to-clipboard';
import { getProducts, getAllProductSkus } from 'util/api';
import { MAILCHIMP_SKU_COLUMNS } from 'config/mailchimpSkuColumns';
import {
  groupSkusByProductMatrixId,
  skuSlotMapForProduct,
  productMatchesPriceRange,
  productAbvNumeric,
} from 'util/mailchimpSkuColumnMap';
import {
  skuHasTierValue,
  isFoodstuffsPricingType,
} from 'util/mailchimpSkuPrice';
import SnippetBlock from './SnippetBlock/SnippetBlock';
import ProductSnippetSku from './snippets/ProductSnippetSku';
import IntroSnippet from './snippets/IntroSnippet';

const { Text } = Typography;
const { Search } = Input;

const GROUP_LABELS = { slab: 'Slabs', keg: 'Kegs', bottle: 'Bottles' };

function formatAbvDisplay(p) {
  if (p.abv != null && p.abv !== '') return `${p.abv}%`;
  const min = p.abvMin;
  const max = p.abvMax;
  if ((min != null && min !== '') || (max != null && max !== '')) {
    const a = min != null && min !== '' ? `${min}%` : '?';
    const b = max != null && max !== '' ? `${max}%` : '?';
    return `${a} – ${b}`;
  }
  return '—';
}

/** Table/preview hero URL — mirrors ProductSnippetSku fallback order. */
function productHeroImageSrc(product, source) {
  const web = product.webRenderUrl;
  const tap = product.tapBadgeUrl;
  if (source === 'webRender') return web || tap || undefined;
  return tap || web || undefined;
}

const MailchimpNewBuilder = () => {
  const [products, setProducts] = useState([]);
  const [allSkus, setAllSkus] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [styleFilter, setStyleFilter] = useState([]);
  const [abvMin, setAbvMin] = useState(null);
  const [abvMax, setAbvMax] = useState(null);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);

  /** Column ids to hide (empty = all MAILCHIMP_SKU_COLUMNS visible). */
  const [columnOffIds, setColumnOffIds] = useState([]);

  const [pricingType, setPricingType] = useState('onPremise');
  const [generateIntro, setGenerateIntro] = useState(false);
  const [showCode, setShowCode] = useState(false);
  /** Hero image: on-prem → tap badge; any other tier → web render (overridable). */
  const [productImageSource, setProductImageSource] = useState('tapBadge');

  /** @type {Record<string, Record<string, number>>} productId -> columnId -> skuKey */
  const [selection, setSelection] = useState({});
  const [snippetConfig, setSnippetConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pList, skus] = await Promise.all([getProducts(), getAllProductSkus()]);
        if (cancelled) return;
        setProducts(Array.isArray(pList) ? pList : []);
        setAllSkus(Array.isArray(skus) ? skus : []);
      } catch (e) {
        message.error('Failed to load products or SKUs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setProductImageSource(pricingType === 'onPremise' ? 'tapBadge' : 'webRender');
  }, [pricingType]);

  useEffect(() => {
    if (isFoodstuffsPricingType(pricingType)) {
      setPriceMin(null);
      setPriceMax(null);
    }
  }, [pricingType]);

  const enrichedProducts = useMemo(() => {
    const byPid = groupSkusByProductMatrixId(allSkus);
    return (products || [])
      .map((p) => {
        const pid = String(p.id);
        const skus = byPid.get(pid) || [];
        return {
          ...p,
          skus,
          mailchimpSkuSlots: skuSlotMapForProduct(skus),
        };
      })
      .filter((p) => (p.skus || []).length > 0);
  }, [products, allSkus]);

  const distinctStyles = useMemo(() => {
    const s = new Set();
    enrichedProducts.forEach((p) => {
      if (p.style != null && p.style !== '') s.add(p.style);
    });
    return [...s].sort((a, b) => String(a).localeCompare(String(b)));
  }, [enrichedProducts]);

  const visibleColumns = useMemo(
    () => MAILCHIMP_SKU_COLUMNS.filter((c) => !columnOffIds.includes(c.id)),
    [columnOffIds],
  );

  const filteredProducts = useMemo(
    () => enrichedProducts.filter((p) => {
      const q = searchText.trim().toLowerCase();
      if (q) {
        const name = (p.name || '').toLowerCase();
        const ol = (p.oneLiner || '').toLowerCase();
        if (!name.includes(q) && !ol.includes(q)) return false;
      }
      if (styleFilter.length && !styleFilter.includes(p.style)) return false;

      const abv = productAbvNumeric(p);
      if (abvMin != null && Number.isFinite(abvMin)) {
        if (abv == null || abv < abvMin) return false;
      }
      if (abvMax != null && Number.isFinite(abvMax)) {
        if (abv == null || abv > abvMax) return false;
      }

      if (!productMatchesPriceRange(p.skus, pricingType, priceMin, priceMax)) {
        return false;
      }
      return true;
    }),
    [
      enrichedProducts,
      searchText,
      styleFilter,
      abvMin,
      abvMax,
      priceMin,
      priceMax,
      pricingType,
    ],
  );

  const hasSelection = useMemo(() => (
    Object.values(selection).some((cols) => cols && Object.keys(cols).length > 0)
  ), [selection]);

  const toggleCell = useCallback((productId, columnId, sku, checked) => {
    const pid = String(productId);
    setSelection((prev) => {
      const row = { ...(prev[pid] || {}) };
      if (!checked) {
        delete row[columnId];
      } else if (sku) {
        row[columnId] = Number(sku.key);
      }
      const next = { ...prev };
      if (Object.keys(row).length) next[pid] = row;
      else delete next[pid];
      return next;
    });
  }, []);

  const selectRowVisible = useCallback((p) => {
    const slots = p.mailchimpSkuSlots;
    const inner = visibleColumns.reduce((acc, col) => {
      const sku = slots[col.id];
      if (sku) acc[col.id] = Number(sku.key);
      return acc;
    }, {});
    setSelection((prev) => ({ ...prev, [String(p.id)]: inner }));
  }, [visibleColumns]);

  const clearRow = useCallback((p) => {
    setSelection((prev) => {
      const next = { ...prev };
      delete next[String(p.id)];
      return next;
    });
  }, []);

  const onPreview = useCallback(() => {
    const formData = {
      pricingType,
      generateIntro,
      showCode,
      productImageSource,
    };
    const enriched = enrichedProducts
      .map((p) => {
        const pid = String(p.id);
        const row = selection[pid];
        if (!row || !Object.keys(row).length) return null;
        const keySet = new Set(Object.values(row).map(Number));
        const includedSkus = (p.skus || []).filter((s) => keySet.has(Number(s.key)));
        if (!includedSkus.length) return null;
        const { mailchimpSkuSlots: _slots, ...productForSnippet } = p;
        return { ...productForSnippet, includedSkus };
      })
      .filter(Boolean);
    if (!enriched.length) {
      message.warning('Select at least one SKU column for one product.');
      return;
    }
    setSnippetConfig({ products: enriched, formData });
  }, [enrichedProducts, selection, pricingType, generateIntro, showCode, productImageSource]);

  const onCopyEmail = () => {
    copyElementToClipboard({ elementId: 'email-snippets-container-new' });
    message.success('Email copied to clipboard!');
  };

  const columns = useMemo(() => {
    const fixed = [
      {
        title: '',
        key: 'badge',
        fixed: 'left',
        width: 56,
        render: (_, p) => (
          <Avatar
            src={productHeroImageSrc(p, productImageSource)}
            size={40}
            shape="square"
            icon={<QuestionOutlined />}
          />
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 200,
        ellipsis: true,
      },
      {
        title: 'Style',
        dataIndex: 'style',
        key: 'style',
        fixed: 'left',
        width: 120,
        ellipsis: true,
        render: (v) => v ?? '—',
      },
      {
        title: 'ABV',
        key: 'abv',
        fixed: 'left',
        width: 88,
        render: (_, p) => formatAbvDisplay(p),
      },
      {
        title: '',
        key: 'rowActions',
        fixed: 'left',
        width: 104,
        render: (_, p) => (
          <Space size="small">
            <Button type="link" size="small" onClick={() => selectRowVisible(p)}>
              All
            </Button>
            <Button type="link" size="small" onClick={() => clearRow(p)}>
              Clear
            </Button>
          </Space>
        ),
      },
    ];

    const slotCols = visibleColumns.map((col) => ({
      title: col.label,
      key: col.id,
      width: 112,
      align: 'center',
      render: (_, p) => {
        const sku = p.mailchimpSkuSlots[col.id];
        const pid = String(p.id);
        const checked = sku && selection[pid]?.[col.id] === Number(sku.key);
        if (!sku) return <Text type="secondary">—</Text>;
        const hasTierValue = skuHasTierValue(sku, pricingType);
        return (
          <Checkbox
            className={
              hasTierValue ? undefined : 'mailchimp-new-builder-sku-checkbox--no-price'
            }
            checked={!!checked}
            onChange={(e) => toggleCell(p.id, col.id, sku, e.target.checked)}
          />
        );
      },
    }));

    return [...fixed, ...slotCols];
  }, [visibleColumns, selection, toggleCell, selectRowVisible, clearRow, pricingType, productImageSource]);

  const visibleColumnSelectValue = useMemo(
    () => MAILCHIMP_SKU_COLUMNS
      .filter((c) => !columnOffIds.includes(c.id))
      .map((c) => c.id),
    [columnOffIds],
  );

  const columnVisibilityOptions = useMemo(
    () => MAILCHIMP_SKU_COLUMNS.map((c) => ({
      label: `${c.label} (${GROUP_LABELS[c.group]})`,
      value: c.id,
    })),
    [],
  );

  const onVisibleColumnsChange = useCallback((selectedIds) => {
    if (selectedIds == null || selectedIds.length === 0) {
      setColumnOffIds([]);
      return;
    }
    const sel = new Set(selectedIds);
    setColumnOffIds(
      MAILCHIMP_SKU_COLUMNS.filter((c) => !sel.has(c.id)).map((c) => c.id),
    );
  }, []);

  if (loading) {
    return (
      <div className="mailchimp-new-builder mailchimp-new-builder--loading">
        <Spin size="large" />
      </div>
    );
  }

  const sapTier = isFoodstuffsPricingType(pricingType);

  return (
    <div className="mailchimp-new-builder">
      <Card className="mailchimp-new-builder-toolbar" size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="center">
            <Text>Pricing tier</Text>
            <Select
              value={pricingType}
              onChange={setPricingType}
              style={{ minWidth: 200 }}
            >
              <Select.Option value="onPremise">On Premise</Select.Option>
              <Select.Option value="offPremise">Off Premise</Select.Option>
              <Select.Option value="progressive">Progressive</Select.Option>
              <Select.Option value="vicSa">VIC &amp; SA</Select.Option>
              <Select.Option value="qldWa">QLD &amp; WA</Select.Option>
              <Select.Option value="australia">Australia (general)</Select.Option>
              <Select.Option value="foodstuffsNi">Foodstuffs NI</Select.Option>
              <Select.Option value="foodstuffsSi">Foodstuffs SI</Select.Option>
              <Select.Option value="foodstuffsBoth">Foodstuffs NI &amp; SI</Select.Option>
            </Select>
            <Text>Product image</Text>
            <Radio.Group
              value={productImageSource}
              onChange={(e) => setProductImageSource(e.target.value)}
            >
              <Radio.Button value="tapBadge">Tap badge</Radio.Button>
              <Radio.Button value="webRender">Web render</Radio.Button>
            </Radio.Group>
            <Text>Generate intro</Text>
            <Switch checked={generateIntro} onChange={setGenerateIntro} />
            <Text>Show code snippets</Text>
            <Switch checked={showCode} onChange={setShowCode} />
            <Button type="primary" onClick={onPreview} disabled={!hasSelection}>
              Generate
            </Button>
          </Space>
        </Space>
      </Card>

      <Card className="mailchimp-new-builder-filters" bordered={false}>
        <Row gutter={[16, 16]} align="top">
          <Col xs={24} lg={6} xl={7}>
            <Text type="secondary" className="mailchimp-new-builder-filter-label">Search</Text>
            <Search
              placeholder="Name or one-liner"
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Text type="secondary" className="mailchimp-new-builder-filter-label">Style</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="All styles"
              value={styleFilter}
              onChange={setStyleFilter}
              style={{ width: '100%' }}
              options={distinctStyles.map((s) => ({ label: s, value: s }))}
            />
          </Col>
          <Col xs={24} sm={12} lg={7} xl={6}>
            <Text type="secondary" className="mailchimp-new-builder-filter-label">ABV %</Text>
            <div className="mailchimp-new-builder-filter-range">
              <InputNumber
                className="mailchimp-new-builder-filter-number"
                min={0}
                max={100}
                placeholder="Min"
                value={abvMin}
                onChange={setAbvMin}
              />
              <Text type="secondary" className="mailchimp-new-builder-filter-sep">—</Text>
              <InputNumber
                className="mailchimp-new-builder-filter-number"
                min={0}
                max={100}
                placeholder="Max"
                value={abvMax}
                onChange={setAbvMax}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} lg={7} xl={7}>
            <Text type="secondary" className="mailchimp-new-builder-filter-label">Trade price</Text>
            <div className="mailchimp-new-builder-filter-range">
              <InputNumber
                className="mailchimp-new-builder-filter-number"
                min={0}
                placeholder="Min"
                value={priceMin}
                onChange={setPriceMin}
                disabled={sapTier}
              />
              <Text type="secondary" className="mailchimp-new-builder-filter-sep">—</Text>
              <InputNumber
                className="mailchimp-new-builder-filter-number"
                min={0}
                placeholder="Max"
                value={priceMax}
                onChange={setPriceMax}
                disabled={sapTier}
              />
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16 }}>
          <Col span={24}>
            <Text type="secondary" className="mailchimp-new-builder-filter-label">Format columns</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="All formats shown"
              value={visibleColumnSelectValue}
              onChange={onVisibleColumnsChange}
              options={columnVisibilityOptions}
              style={{ width: '100%' }}
              maxTagCount="responsive"
            />
          </Col>
        </Row>
      </Card>

      <Card size="small" className="mailchimp-new-builder-table-card">
        <Table
          size="small"
          rowKey={(p) => String(p.id)}
          dataSource={filteredProducts}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 780 + visibleColumns.length * 112 }}
        />
      </Card>

      <CustomModal
        title="Copy stock update email"
        centered
        width="90%"
        visible={!!snippetConfig}
        footer={[
          (!snippetConfig?.formData.showCode ? (
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={onCopyEmail}
            >
              Copy email to clipboard
            </Button>
          ) : null),
          <Button type="primary" key="done" onClick={() => setSnippetConfig(null)}>Done</Button>,
        ]}
        onCancel={() => setSnippetConfig(null)}
      >
        <div id="email-snippets-container-new">
          {snippetConfig?.formData.generateIntro && (
            <>
              <SnippetBlock
                id="email-intro"
                showCode={snippetConfig?.formData.showCode}
              >
                <IntroSnippet />
              </SnippetBlock>
              <Divider />
            </>
          )}
          {snippetConfig?.products?.map((product, index) => (
            <React.Fragment key={product.id}>
              <SnippetBlock
                id={product.id}
                showCode={snippetConfig?.formData?.showCode}
              >
                <ProductSnippetSku
                  pricingType={snippetConfig.formData.pricingType}
                  productImageSource={snippetConfig.formData.productImageSource}
                  product={product}
                />
              </SnippetBlock>
              {index < (snippetConfig?.products?.length ?? 0) - 1 && <Divider />}
            </React.Fragment>
          ))}
        </div>
      </CustomModal>
    </div>
  );
};

export default MailchimpNewBuilder;
